import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AlbetheineEye from "@/components/AlbetheineEye";
import CelticOrnament from "@/components/CelticOrnament";
import StonesIllustration from "@/components/StonesIllustration";
import CandleIllustration from "@/components/CandleIllustration";
import FoundersCircle from "@/components/FoundersCircle";
import WhispersGallery from "@/components/WhispersGallery";

// Legacy SVG illustrations — kept available as graceful fallbacks but
// superseded by the user's AI imagery below.
void StonesIllustration;
void CandleIllustration;

// Live link to the Albetheine Oracle mobile app (Expo web build).
// Works on phone, laptop and tablet — no install required.
const APP_INSTALL_URL = "https://candlelit-cards-1.preview.emergentagent.com/sanctum";

// User-supplied AI imagery (each carries its own quote / wisdom).
// Mapping verified from upload timestamps + visible content.
const IMG_STONES =
  "https://customer-assets.emergentagent.com/job_missing-deploy-3/artifacts/00sy7l58_Screenshot_20260515_175846_Gallery.jpg";
const IMG_CANDLE =
  "https://customer-assets.emergentagent.com/job_missing-deploy-3/artifacts/lpx7ngsm_Screenshot_20260515_175901_Gallery.jpg";
const IMG_HANDS =
  "https://customer-assets.emergentagent.com/job_missing-deploy-3/artifacts/t0wy9q4l_Screenshot_20260515_175941_Gallery.jpg";
const IMG_DOOR =
  "https://customer-assets.emergentagent.com/job_missing-deploy-3/artifacts/fimiqgkz_Screenshot_20260515_180006_Gallery.jpg";

const CANDLE_IMG =
  "https://images.unsplash.com/photo-1518562180175-34a163b1a9a6?auto=format&fit=crop&w=1600&q=80";

const offerings = [
  {
    tag: "MOBILE APP",
    title: "Albetheine Oracle App",
    description:
      "A devotional pocket sanctuary. Pull one card daily, journal your reflections, and meet Albetheine wherever you are.",
    perk: "Seven free single-card pulls included on sign-up.",
    cta: "Open the Oracle",
    href: "__APP__",
    available: true,
  },
  {
    tag: "WRITTEN COMPANION",
    title: "Albetheine Online Guidebook",
    description:
      "The full written companion to Albetheine's system — symbol lore, card meanings, ritual practices and the cosmology behind the deck.",
    perk: "Lifetime access. Read anywhere.",
    cta: "Notify Me at Launch",
    href: "#founders-circle",
    available: true,
  },
  {
    tag: "PSYCHIC MEDIUMSHIP",
    title: "One-to-One Online Readings",
    description:
      "Private mediumship sessions held online. Compassionate, evidential and rooted in Albetheine's wisdom — for guidance, connection and clarity.",
    perk: "60 minutes · held over secure video.",
    cta: "Enquire About a Session",
    href: "mailto:tappedinclarity@gmail.com?subject=Psychic%20Medium%20Reading%20Enquiry",
    available: true,
  },
  {
    tag: "PHYSICAL DECKS · COMING SOON",
    title: "Albetheine's Beacon Oracle Deck",
    description:
      "The light-bearer deck. Hand-illustrated cards channelling the Beacon current — for direction, courage and the path forward.",
    perk: "Print-on-demand · ships worldwide on release.",
    cta: "Join the Waiting List",
    href: "#founders-circle",
    available: false,
  },
  {
    tag: "PHYSICAL DECKS · COMING SOON",
    title: "Albetheine's Shadow Oracle Deck",
    description:
      "The shadow companion deck. For the work beneath — integration, ancestral healing, and the gold buried in the dark.",
    perk: "Print-on-demand · ships worldwide on release.",
    cta: "Join the Waiting List",
    href: "#founders-circle",
    available: false,
  },
];

const pricing = [
  {
    name: "Albetheine Oracle App",
    price: "£9.99",
    cadence: "every 3 months",
    line1: "Unlimited card pulls, journaling and rituals",
    line2: "After your seven free pulls",
    testid: "price-app",
  },
  {
    name: "Online Guidebook",
    price: "£27",
    cadence: "one-time · lifetime access",
    line1: "The complete written Albetheine companion",
    line2: "Symbols · meanings · cosmology",
    testid: "price-guidebook",
  },
  {
    name: "Guidebook + 3 Months App",
    price: "£34.99",
    cadence: "one-time bundle",
    line1: "The Guidebook plus three months of the app",
    line2: "Most chosen by new readers",
    highlight: true,
    testid: "price-bundle",
  },
  {
    name: "Psychic Medium Reading",
    price: "£150",
    cadence: "per 60-minute session",
    line1: "Private 1:1 online mediumship",
    line2: "By booking only · waiting list available",
    testid: "price-reading",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src={CANDLE_IMG}
            alt=""
            onError={(e) => (e.currentTarget.style.display = "none")}
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 candle-glow" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 md:pb-36 grain-overlay">
          <div className="flex flex-col items-center text-center">
            <div className="flicker">
              <AlbetheineEye size={140} />
            </div>

            <div className="eyebrow mt-8 fade-up">
              TAPPED IN CLARITY · UNITED KINGDOM
            </div>

            <h1
              className="brand-name text-cream text-5xl md:text-7xl lg:text-8xl mt-5 fade-up delay-1"
              data-testid="hero-brand"
            >
              Tapped In Clarity
            </h1>

            <CelticOrnament size={90} className="mt-6 opacity-80 fade-up delay-2" />

            <h2
              className="font-cinzel text-gold-bright text-2xl md:text-4xl lg:text-5xl mt-6 leading-tight fade-up delay-2"
              data-testid="hero-tagline"
            >
              Albetheine&apos;s Oracle
            </h2>

            <p
              className="font-cormorant italic text-cream-soft text-xl md:text-2xl max-w-2xl mt-7 leading-relaxed fade-up delay-3"
            >
              A candlelit modern oracle — app, guidebook, hand-illustrated decks
              and one-to-one mediumship — drawn from ancestral light and the
              quiet wisdom beneath the stone circles.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-12 fade-up delay-4">
              <a
                href={APP_INSTALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid="hero-cta-get-app"
              >
                Open the Oracle
              </a>
              <a
                href="#offerings"
                className="btn-ghost"
                data-testid="hero-cta-offerings"
              >
                Explore Offerings
              </a>
              <a
                href="#founders-circle"
                className="btn-ghost"
                data-testid="hero-cta-founders"
              >
                Join Founder&apos;s Circle
              </a>
            </div>

            <div className="mt-10 text-cream-muted text-xs font-cinzel tracking-[0.32em] fade-up delay-5">
              · SEVEN FREE CARD PULLS ON SIGN UP ·
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section
        id="about"
        className="relative bg-charcoal py-24 md:py-32 overflow-hidden"
        data-testid="about-section"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow">A QUIET INVITATION</div>
            <h2 className="section-heading text-3xl md:text-5xl mt-5 leading-tight">
              An oracle for those who have always{" "}
              <span className="gold-gradient-text">felt the older voices</span>.
            </h2>
            <div className="gold-divider my-8 max-w-xs" />
            <p className="text-cream-soft text-lg leading-relaxed">
              Tapped In Clarity is the home of <em>Albetheine&apos;s Oracle</em> —
              a modern devotional system woven from candlelight, stone-circle
              memory and lemurian symbolry. Albetheine is the guardian of the
              deck and the keeper of its knowledge — not a gimmick, but a
              quiet, luxurious tool for people who already know there is more
              than the eye sees.
            </p>
            <p className="text-cream-soft text-lg leading-relaxed mt-4">
              The work lives in a mobile app, a written guidebook, two
              hand-illustrated decks, and private mediumship sessions held with
              reverence and care.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="relative card-occult p-2 aspect-[4/5] overflow-hidden">
              <img
                src={IMG_STONES}
                alt="Standing stones at sunset — 'You belong to something ancient'"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <span className="corner-ornament corner-tl" />
              <span className="corner-ornament corner-tr" />
              <span className="corner-ornament corner-bl" />
              <span className="corner-ornament corner-br" />
            </div>
          </div>
        </div>
      </section>

      {/* OFFERINGS */}
      <section
        id="offerings"
        className="relative bg-charcoal-2 py-24 md:py-32 border-y border-[rgba(201,169,97,0.18)]"
        data-testid="offerings-section"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <div className="eyebrow">WHAT WE OFFER</div>
            <h2 className="section-heading text-3xl md:text-5xl mt-5">
              The <span className="gold-gradient-text">Five Lights</span> of the
              Sanctuary
            </h2>
            <CelticOrnament size={70} className="mx-auto mt-6 opacity-70" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            {offerings.map((o, i) => (
              <article
                key={o.title}
                className="relative card-occult p-8 md:p-10"
                data-testid={`offering-card-${i}`}
              >
                <span className="corner-ornament corner-tl" />
                <span className="corner-ornament corner-tr" />
                <span className="corner-ornament corner-bl" />
                <span className="corner-ornament corner-br" />
                <div className="font-cinzel text-gold text-[0.7rem] tracking-[0.32em]">
                  {o.tag}
                </div>
                <h3 className="font-cinzel text-cream text-2xl mt-4 leading-snug">
                  {o.title}
                </h3>
                <p className="text-cream-soft mt-4 leading-relaxed">
                  {o.description}
                </p>
                <div className="gold-divider my-6" />
                <p className="font-cormorant italic text-gold-bright text-lg">
                  {o.perk}
                </p>
                <div className="mt-6 flex items-center justify-between gap-4">
                  <div className="font-cinzel text-[0.7rem] tracking-[0.28em] text-cream-muted">
                    {o.available ? "·  AVAILABLE  ·" : "·  COMING SOON  ·"}
                  </div>
                  {o.cta && (
                    <a
                      href={o.href === "__APP__" ? APP_INSTALL_URL : o.href}
                      target={o.href === "__APP__" ? "_blank" : undefined}
                      rel={o.href === "__APP__" ? "noopener noreferrer" : undefined}
                      className="font-cinzel text-[0.7rem] tracking-[0.22em] text-gold-bright hover:text-cream transition-colors"
                      data-testid={`offering-cta-${i}`}
                    >
                      {o.cta.toUpperCase()} →
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="relative bg-charcoal py-24 md:py-32 overflow-hidden"
        data-testid="how-section"
      >
        <div className="absolute inset-0 -z-10 opacity-10 flex items-center justify-center">
          <div className="slow-rotate">
            <AlbetheineEye size={620} />
          </div>
        </div>

        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <div className="eyebrow">HOW IT BEGINS</div>
            <h2 className="section-heading text-3xl md:text-5xl mt-5">
              Seven cards.{" "}
              <span className="gold-gradient-text">One quiet doorway.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {[
              {
                n: "I",
                t: "Open the App",
                d: "Install Albetheine's Oracle on your phone and create a free sanctuary account.",
              },
              {
                n: "II",
                t: "Seven Free Pulls",
                d: "Begin with seven single-card pulls — daily guidance from Albetheine.",
              },
              {
                n: "III",
                t: "Upgrade When Ready",
                d: "Unlock unlimited pulls, deeper spreads, journaling and rituals from £9.99 every 3 months.",
              },
            ].map((s) => (
              <div key={s.n} className="text-center">
                <div className="font-cinzel text-gold-bright text-5xl tracking-[0.2em]">
                  {s.n}
                </div>
                <div className="gold-divider my-6 max-w-[120px] mx-auto" />
                <div className="font-cinzel text-cream text-lg tracking-[0.18em]">
                  {s.t}
                </div>
                <p className="text-cream-soft mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section
        id="pricing"
        className="relative bg-charcoal-2 py-24 md:py-32 border-y border-[rgba(201,169,97,0.18)]"
        data-testid="pricing-section"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <div className="eyebrow">OFFERINGS & PRICING</div>
            <h2 className="section-heading text-3xl md:text-5xl mt-5">
              Reverent prices.{" "}
              <span className="gold-gradient-text">Real craft.</span>
            </h2>
            <p className="text-cream-soft mt-6 max-w-2xl mx-auto leading-relaxed">
              All payments are in GBP (£) and are processed securely. Digital
              goods are non-refundable; physical decks follow our{" "}
              <Link to="/refund" className="app-link">
                refund policy
              </Link>
              .
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricing.map((p) => (
              <div
                key={p.name}
                className={`relative card-occult p-8 flex flex-col ${
                  p.highlight ? "border-[rgba(201,169,97,0.55)]" : ""
                }`}
                data-testid={p.testid}
              >
                <span className="corner-ornament corner-tl" />
                <span className="corner-ornament corner-tr" />
                <span className="corner-ornament corner-bl" />
                <span className="corner-ornament corner-br" />
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-charcoal px-3 py-1 font-cinzel text-[0.62rem] tracking-[0.3em] text-gold-bright border border-[rgba(201,169,97,0.55)]">
                    MOST CHOSEN
                  </div>
                )}
                <div className="font-cinzel text-gold text-[0.7rem] tracking-[0.3em] min-h-[1.4rem]">
                  {p.name.toUpperCase()}
                </div>
                <div className="mt-6 brand-name gold-gradient-text text-5xl">
                  {p.price}
                </div>
                <div className="font-cinzel text-cream-muted text-[0.7rem] tracking-[0.24em] mt-2">
                  {p.cadence.toUpperCase()}
                </div>
                <div className="gold-divider my-6" />
                <div className="text-cream-soft text-sm leading-relaxed flex-1">
                  <div>{p.line1}</div>
                  <div className="text-cream-muted mt-2 italic">{p.line2}</div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-cream-muted text-xs font-cinzel tracking-[0.26em] mt-12">
            · CHECKOUT OPENS WHEN PAYMENTS GO LIVE · YOU'LL BE THE FIRST TO KNOW ·
          </p>
        </div>
      </section>

      {/* IMAGERY BAND — user candle image (carries its own wisdom text) */}
      <section className="relative h-[55vh] md:h-[80vh] overflow-hidden bg-charcoal">
        <img
          src={IMG_CANDLE}
          alt="A single candle and clear crystal on a stone sill — 'Something is working on your behalf'"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-transparent to-charcoal/60 pointer-events-none" />
      </section>

      {/* WHISPERS FROM ALBETHEINE — user-supplied wisdom imagery */}
      <WhispersGallery />

      {/* FOUNDER'S CIRCLE */}
      <FoundersCircle />

      {/* CONTACT / CTA */}
      <section
        id="contact"
        className="relative bg-charcoal py-24 md:py-32"
        data-testid="contact-section"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <AlbetheineEye size={80} className="mx-auto opacity-90" />
          <div className="eyebrow mt-6">SPEAK WITH US</div>
          <h2 className="section-heading text-3xl md:text-5xl mt-5 leading-tight">
            For readings, deck enquiries{" "}
            <span className="gold-gradient-text">or quiet questions.</span>
          </h2>
          <p className="text-cream-soft mt-7 leading-relaxed">
            Tapped In Clarity is run by a UK-based sole trader. Every message is
            read personally. Please allow up to{" "}
            <strong className="text-cream">3 working days</strong> for a reply.
          </p>

          <a
            href="mailto:tappedinclarity@gmail.com"
            className="btn-gold mt-10 inline-flex"
            data-testid="contact-email-cta"
          >
            tappedinclarity@gmail.com
          </a>

          <div className="mt-10 text-cream-muted text-xs font-cinzel tracking-[0.28em]">
            · BUSINESS ADDRESS AVAILABLE ON REQUEST ·
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
